/**
 * useBotConfig — shared data hook for every v2 bot configuration sub-page.
 *
 * Owns GET `/api/bot/{id}/config` → editable state → PUT `/api/bot/{id}/config`.
 * Pages call `update(key, value)` to stage changes and `save()` to commit.
 *
 * Same endpoint + auth as legacy ConfigPage (`useApiHeaders`), so this is a
 * pure additive — zero backend changes.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useApiHeaders } from '../../hooks/useApiHeaders';
import type { BotConfig } from '../../config/types';
import {
  announceBotPublished,
  applyConfigDraftPatch,
  diffConfigDraft,
  readConfigDraft,
  writeConfigDraft,
} from './draftStorage';

export interface UseBotConfigResult {
  /** Current editable draft of the config. */
  config: Partial<BotConfig>;
  /**
   * The last fetched/saved config — what the server holds. The unpublished
   * changes summary diffs `config` against this. Optional so hand-built test
   * controllers needn't supply it.
   */
  savedConfig?: Partial<BotConfig>;
  /** Patch a single field in the draft. */
  update: <K extends keyof BotConfig>(key: K, value: BotConfig[K]) => void;
  /** Patch many fields at once. */
  updateMany: (patch: Partial<BotConfig>) => void;
  /** Merge fields already saved outside the main Publish flow into both snapshots. */
  applySavedPatch?: (patch: Partial<BotConfig>) => void;
  /** PUT the draft to the backend. Returns true on success. */
  save: () => Promise<boolean>;
  /** Re-fetch from backend, discarding local edits. */
  refresh: () => Promise<void>;
  loading: boolean;
  saving: boolean;
  notFound: boolean;
  error: string | null;
  errorActionHref: string | null;
  errorActionLabel: string | null;
  success: string | null;
  clearStatus: () => void;
  /** True iff the draft differs from the last fetched/saved snapshot. */
  dirty: boolean;
}

async function readSaveError(res: Response): Promise<{
  message: string;
  actionHref: string | null;
  actionLabel: string | null;
}> {
  const text = await res.text();
  try {
    const data = text ? JSON.parse(text) : null;
    if (data && typeof data === 'object') {
      const obj = data as { error?: unknown; editor_url?: unknown };
      const message = typeof obj.error === 'string' && obj.error.trim()
        ? obj.error
        : text || `HTTP ${res.status}`;
      const actionHref = typeof obj.editor_url === 'string' && obj.editor_url.trim()
        ? obj.editor_url
        : null;
      return {
        message,
        actionHref,
        actionLabel: actionHref ? 'Fix in conversation graph editor' : null,
      };
    }
  } catch {
    // Fall through to plain text below.
  }
  return { message: text || `HTTP ${res.status}`, actionHref: null, actionLabel: null };
}

export function useBotConfig(
  botId: string | null | undefined,
  opts?: { autoFetch?: boolean },
): UseBotConfigResult {
  // autoFetch defaults to true so existing callers keep fetching on mount.
  // The hoisted BotConfigProvider passes `false` and triggers the (single)
  // fetch lazily, only once a consumer (Configure) actually mounts — so
  // sibling bot pages (Operate/CallLogs/BotHero) make no extra config call.
  const autoFetch = opts?.autoFetch ?? true;
  const apiHeaders = useApiHeaders('');
  const [config, setConfig] = useState<Partial<BotConfig>>({});
  const [savedConfig, setSavedConfig] = useState<Partial<BotConfig>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorActionHref, setErrorActionHref] = useState<string | null>(null);
  const [errorActionLabel, setErrorActionLabel] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  // Set once the first GET has landed; the persist effect below stays quiet
  // before that so an empty pre-load draft can't wipe a stored one.
  const loadedRef = useRef(false);
  // The persisted draft is overlaid exactly once — on the first load. Later
  // refreshes (after Publish, or a history restore) are deliberate resyncs.
  const restoredRef = useRef(false);

  const refresh = useCallback(async () => {
    if (!botId) return;
    abortRef.current?.abort();
    const ac = new AbortController();
    abortRef.current = ac;
    setLoading(true);
    setError(null);
    setErrorActionHref(null);
    setErrorActionLabel(null);
    try {
      const res = await fetch(`/api/bot/${botId}/config`, {
        headers: apiHeaders(),
        cache: 'no-store',
        signal: ac.signal,
      });
      if (res.status === 404) { setNotFound(true); setLoading(false); return; }
      if (!res.ok) {
        const details = await readSaveError(res);
        throw Object.assign(new Error(details.message), {
          actionHref: details.actionHref,
          actionLabel: details.actionLabel,
        });
      }
      const data: BotConfig = await res.json();
      // Mirror the legacy defaults so the UI shows the same as ConfigPage.
      const normalized: Partial<BotConfig> = {
        ...data,
        // Only default the fallback PROVIDER to the legacy 'openai' when no
        // fallback model is configured. An explicitly-saved Gemini fallback
        // stores provider='' + a model id (e.g. 'gemini-3.5-flash'); coercing
        // its empty provider to 'openai' corrupts the (provider, model) pair so
        // it matches no model in the picker.
        llm_provider_priority_2:
          data.llm_provider_priority_2 || (data.llm_model_priority_2 ? '' : 'openai'),
        llm_model_priority_2: data.llm_model_priority_2 || '',
        openai_realtime_model_priority_2: data.openai_realtime_model_priority_2 || 'gpt-realtime-2025-08-28',
      };
      let draft: Partial<BotConfig> = normalized;
      if (!restoredRef.current) {
        restoredRef.current = true;
        const patch = readConfigDraft(botId);
        if (patch) draft = applyConfigDraftPatch(normalized, patch);
      }
      loadedRef.current = true;
      setSavedConfig(normalized);
      setConfig(draft);
      setNotFound(false);
    } catch (err: any) {
      if (err.name === 'AbortError') return;
      setError(err.message || String(err));
      setErrorActionHref(typeof err.actionHref === 'string' ? err.actionHref : null);
      setErrorActionLabel(typeof err.actionLabel === 'string' ? err.actionLabel : null);
    } finally {
      setLoading(false);
    }
  }, [botId, apiHeaders]);

  useEffect(() => { if (autoFetch) void refresh(); }, [refresh, autoFetch]);

  // Persist the unpublished changes (as a patch against the saved snapshot) so
  // a reload restores them. Clears the entry when the draft is clean, which is
  // also what removes it after a successful Publish or a `refresh()` resync.
  useEffect(() => {
    if (!botId || !loadedRef.current) return;
    writeConfigDraft(botId, diffConfigDraft(config, savedConfig));
  }, [botId, config, savedConfig]);

  const update = useCallback(<K extends keyof BotConfig>(key: K, value: BotConfig[K]) => {
    setConfig((prev) => ({ ...prev, [key]: value }));
  }, []);

  const updateMany = useCallback((patch: Partial<BotConfig>) => {
    setConfig((prev) => ({ ...prev, ...patch }));
  }, []);

  const applySavedPatch = useCallback((patch: Partial<BotConfig>) => {
    setConfig((prev) => ({ ...prev, ...patch }));
    setSavedConfig((prev) => ({ ...prev, ...patch }));
  }, []);

  const save = useCallback(async (): Promise<boolean> => {
    if (!botId) return false;
    setSaving(true);
    setError(null);
    setErrorActionHref(null);
    setErrorActionLabel(null);
    setSuccess(null);
    try {
      // Strip fields the backend's PUT handler doesn't accept (they come from
      // GET but are read-only / computed). Same set the legacy save uses.
      const { bot_id, customer_id, has_openai_key, has_deepdub_key, has_soniox_key,
              detected_domain, has_asterisk_ari, cloudonix_phone_number,
              ...editable } = config as any;
      const res = await fetch(`/api/bot/${botId}/config`, {
        method: 'PUT',
        headers: { ...apiHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(editable),
      });
      if (!res.ok) {
        const details = await readSaveError(res);
        throw Object.assign(new Error(details.message), {
          actionHref: details.actionHref,
          actionLabel: details.actionLabel,
        });
      }
      // The PUT handler returns only {bot_id, customer_id, updated: N,
      // warnings?: string[]}, not the full config — so we can't trust the
      // response as the new state (doing so wipes glossary/prompts/etc from
      // the in-memory draft until the next page reload). Refetch instead.
      // `warnings` lists admin-only fields the server ignored for a non-admin
      // save; surface them instead of a bare "Saved.".
      let warnings: string[] = [];
      try {
        const body = await res.json();
        if (Array.isArray(body?.warnings)) warnings = body.warnings.filter((w: unknown) => typeof w === 'string');
      } catch { /* non-JSON body → no warnings */ }
      await refresh();
      // The draft is now published: drop the persisted copy before telling
      // other tabs, so a tab that reloads on the signal cannot restore it.
      writeConfigDraft(botId, null);
      announceBotPublished(botId);
      setSuccess(warnings.length ? `Saved. Not applied: ${warnings.join(' ')}` : 'Saved.');
      return true;
    } catch (err: any) {
      setError(err.message || String(err));
      setErrorActionHref(typeof err.actionHref === 'string' ? err.actionHref : null);
      setErrorActionLabel(typeof err.actionLabel === 'string' ? err.actionLabel : null);
      return false;
    } finally {
      setSaving(false);
    }
  }, [botId, config, apiHeaders, refresh]);

  const clearStatus = useCallback(() => {
    setError(null);
    setErrorActionHref(null);
    setErrorActionLabel(null);
    setSuccess(null);
  }, []);

  const dirty = useMemo(
    () => JSON.stringify(config) !== JSON.stringify(savedConfig),
    [config, savedConfig],
  );

  return {
    config,
    savedConfig,
    update,
    updateMany,
    applySavedPatch,
    save,
    refresh,
    loading,
    saving,
    notFound,
    error,
    errorActionHref,
    errorActionLabel,
    success,
    clearStatus,
    dirty,
  };
}
